// Optional styles preserve their cascade position. Demand-loaded links receive
// their URL only when a visible component or an explicit consumer needs them.
const pendingStyles = new WeakMap();
const LOAD_TIMEOUT = 8000;
let retryCount = 0;
let stopSwatchWatching;

function readyStylesheet(link) {
  const previous = pendingStyles.get(link);
  if (previous && !previous.failed) return previous.ready;
  if (previous?.failed && (link.dataset.themeStyleState === 'error' || !link.sheet)) {
    // A later explicit open action can retry a failed request. Replace in place
    // so print/colour overrides keep their original cascade order.
    const retry = link.cloneNode(true);
    const retryUrl = new URL(link.href);
    // Chrome can reuse a failed resource for the same URL. Keep Shopify's
    // version token and request a fresh resource only after an explicit retry.
    retryUrl.searchParams.set('theme_style_retry', String(++retryCount));
    retry.href = retryUrl.href;
    retry.media = 'print';
    delete retry.dataset.themeStyleState;
    link.replaceWith(retry);
    return readyStylesheet(retry);
  }
  const pending = {failed: false, ready: null};
  pendingStyles.set(link, pending);
  pending.ready = new Promise((resolve, reject) => {
    let timeout;
    const cleanup = () => {
      clearTimeout(timeout);
      link.removeEventListener('load', loaded);
      link.removeEventListener('error', failed);
    };
    const loaded = () => {
      cleanup();
      link.media = 'all';
      link.dataset.themeStyleState = 'loaded';
      // Let the newly enabled rules apply before measuring or focusing a panel.
      requestAnimationFrame(resolve);
    };
    const failed = () => {
      cleanup();
      pending.failed = true;
      link.dataset.themeStyleState = 'error';
      reject(new Error(`Unable to load optional stylesheet: ${link.href}`));
    };

    // Attach listeners before assigning the URL, including cache hits.
    if (!link.hasAttribute('href') && link.dataset.themeStyleHref) {
      link.addEventListener('load', loaded, {once: true});
      link.addEventListener('error', failed, {once: true});
      timeout = setTimeout(failed, LOAD_TIMEOUT);
      link.href = link.dataset.themeStyleHref;
    } else if (link.dataset.themeStyleState === 'error') {
      failed();
    } else if (link.sheet) {
      loaded();
    } else {
      link.addEventListener('load', loaded, {once: true});
      link.addEventListener('error', failed, {once: true});
      timeout = setTimeout(failed, LOAD_TIMEOUT);
    }
  });
  return pending.ready;
}

export function ensureDeferredStyles(...groups) {
  const styles = [...document.querySelectorAll('link[data-theme-style-group]')].filter(link =>
    link.dataset.themeStyleGroup.split(/\s+/).some(group => groups.includes(group)),
  );
  const ready = styles.map(readyStylesheet);
  const swatchStyles = ready.filter((promise, index) => styles[index].dataset.themeStyleGroup.split(/\s+/).includes('swatches'));
  if (swatchStyles.length) {
    const release = () => guardedSwatches.forEach((timer, swatch) => releaseSwatch(swatch));
    Promise.all(swatchStyles).then(() => {
      release();
      if (swatchesReady()) stopSwatchWatching?.();
    }, release);
  }
  return Promise.all(ready);
}

export function ensureSwatchStyles(root) {
  return root.querySelector('[data-swatch-value]') ? ensureDeferredStyles('swatches') : Promise.resolve();
}

// One passive attempt per page. An explicit consumer can still retry a failed
// group through ensureDeferredStyles; scrolling must not create a retry storm.
let swatchRequest;
const guardedSwatches = new Map();

function releaseSwatch(swatch) {
  clearTimeout(guardedSwatches.get(swatch));
  guardedSwatches.delete(swatch);
  swatch.removeAttribute('data-theme-swatch-pending');
}

function guardSwatch(swatch) {
  if (guardedSwatches.has(swatch)) return;
  swatch.setAttribute('data-theme-swatch-pending', '');
  guardedSwatches.set(swatch, setTimeout(() => releaseSwatch(swatch), LOAD_TIMEOUT));
}

function swatchesReady() {
  const links = [...document.querySelectorAll('link[data-theme-style-group="swatches"]')];
  return links.length > 0 && links.every(link => link.dataset.themeStyleState === 'loaded');
}

function prepareSwatches() {
  if (!swatchRequest) {
    swatchRequest = ensureDeferredStyles('swatches');
    const release = () => guardedSwatches.forEach((timer, swatch) => releaseSwatch(swatch));
    swatchRequest.then(release, release);
  }
  return swatchRequest;
}

function watchSwatches() {
  document.documentElement.setAttribute('data-theme-swatches-managed', '');
  if (swatchesReady()) {
    document.documentElement.removeAttribute('data-theme-swatches-pending');
    return;
  }
  const hiddenContainer = '.drawer:not(.active), [hidden], [aria-hidden="true"], details:not([open])';
  const available = swatch => swatch.isConnected && swatch.parentElement && !swatch.closest(hiddenContainer) &&
    getComputedStyle(swatch.parentElement).visibility !== 'hidden';
  // Index the ancestors of known controls once. App widgets and carousels make
  // many unrelated class changes; none should trigger a document/subtree scan.
  const ancestorsBySwatch = new Map();
  const swatchesByAncestor = new WeakMap();
  const pendingRechecks = new Set();
  let recheckFrame;
  let stopped = false;
  const forget = swatch => {
    for (const ancestor of ancestorsBySwatch.get(swatch) || []) swatchesByAncestor.get(ancestor)?.delete(swatch);
    ancestorsBySwatch.delete(swatch);
    pendingRechecks.delete(swatch);
    observer?.unobserve(swatch);
    releaseSwatch(swatch);
  };
  const remember = swatch => {
    for (const ancestor of ancestorsBySwatch.get(swatch) || []) swatchesByAncestor.get(ancestor)?.delete(swatch);
    const ancestors = [];
    for (let ancestor = swatch; ancestor; ancestor = ancestor.parentElement) {
      ancestors.push(ancestor);
      if (!swatchesByAncestor.has(ancestor)) swatchesByAncestor.set(ancestor, new Set());
      swatchesByAncestor.get(ancestor).add(swatch);
    }
    ancestorsBySwatch.set(swatch, ancestors);
  };
  const prepare = swatch => {
    observer?.unobserve(swatch);
    guardSwatch(swatch);
    prepareSwatches().then(() => releaseSwatch(swatch), () => releaseSwatch(swatch));
  };
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    if (stopped) return;
    for (const entry of entries) {
      if (entry.isIntersecting && available(entry.target)) prepare(entry.target);
    }
  }, {rootMargin: '400px'}) : null;

  const recheck = swatch => {
    // Hide synchronously, before the first painted frame of an opened panel.
    // Re-observe once per frame so visibility-only changes also receive a new
    // intersection entry, without forcing layout in the mutation callback.
    guardSwatch(swatch);
    if (swatchRequest) {
      swatchRequest.then(() => releaseSwatch(swatch), () => releaseSwatch(swatch));
    } else if (!observer) {
      prepareSwatches();
    } else {
      pendingRechecks.add(swatch);
      if (recheckFrame) return;
      recheckFrame = requestAnimationFrame(() => {
        recheckFrame = null;
        for (const candidate of pendingRechecks) {
          observer.unobserve(candidate);
          if (candidate.isConnected) observer.observe(candidate);
        }
        pendingRechecks.clear();
      });
    }
  };
  const discover = root => {
    const swatches = [...root.querySelectorAll('[data-swatch-value]')];
    if (root.matches?.('[data-swatch-value]')) swatches.unshift(root);
    for (const swatch of swatches) {
      const known = ancestorsBySwatch.has(swatch);
      remember(swatch);
      if (known) {
        // Moving an existing section also changes the ancestors whose visibility
        // matters, even when the swatch's immediate parent stays the same.
        recheck(swatch);
        continue;
      }
      // The initial parser guard hands over to bounded, per-control guards.
      // Hidden drawers and offscreen sections do not start catalogue requests.
      guardSwatch(swatch);
      if (swatchRequest) swatchRequest.then(() => releaseSwatch(swatch), () => releaseSwatch(swatch));
      if (observer) observer.observe(swatch);
      else prepareSwatches();
    }
  };
  discover(document);
  document.documentElement.removeAttribute('data-theme-swatches-pending');
  const mutations = new MutationObserver(records => {
    const changedRoots = new Set();
    const addedRoots = new Set();
    for (const record of records) {
      if (record.type === 'attributes') {
        changedRoots.add(record.target);
        continue;
      }
      for (const node of record.addedNodes) if (node.nodeType === 1 && node.isConnected) addedRoots.add(node);
      for (const node of record.removedNodes) {
        if (node.nodeType !== 1 || node.isConnected) continue;
        for (const swatch of [...(swatchesByAncestor.get(node) || [])]) forget(swatch);
      }
    }
    for (const root of addedRoots) {
      let parent = root.parentElement;
      while (parent && !addedRoots.has(parent)) parent = parent.parentElement;
      if (!parent) discover(root);
    }
    const changedSwatches = new Set();
    for (const root of changedRoots) {
      for (const swatch of swatchesByAncestor.get(root) || []) changedSwatches.add(swatch);
    }
    for (const swatch of changedSwatches) if (swatch.isConnected) recheck(swatch);
  });
  mutations.observe(document.documentElement, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['hidden', 'aria-hidden', 'open', 'class'],
  });

  // Quick-add/variant content can appear before the next intersection callback.
  // Mutation discovery guards newly inserted controls before the next paint.
  const onIntent = e => {
    if (e.target.closest?.('product-card, [data-swatch-value]')?.querySelector('[data-swatch-value]') ||
        e.target.closest?.('[data-swatch-value]')) prepareSwatches();
  };
  for (const event of ['pointerover', 'focusin']) document.addEventListener(event, onIntent, {passive: true});
  stopSwatchWatching = () => {
    stopped = true;
    cancelAnimationFrame(recheckFrame);
    pendingRechecks.clear();
    for (const swatch of ancestorsBySwatch.keys()) forget(swatch);
    observer?.disconnect();
    mutations.disconnect();
    for (const event of ['pointerover', 'focusin']) document.removeEventListener(event, onIntent);
  };
}

if (typeof document !== 'undefined' && document.documentElement) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchSwatches, {once: true});
  } else {
    watchSwatches();
  }
}
